import { useCallback, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import {
  forbiddenPathFor,
  moduleBlockReasonOf,
  type ModuleBlockReason,
} from "@/shared/lib/forbiddenReason";

import { Skeleton } from "@/shared/ui/skeleton";

import { acqService, type AcqCatalogView, type AcqTodayView } from "../api/acqService";
import { AcqShell } from "./AcqShell";
import DiscoverCatalogPage from "./DiscoverCatalogPage";
import TodayPage from "./TodayPage";

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * `/ai-huoke` 的落地分流(契约 §12.8)
 * ═══════════════════════════════════════════════════════════════════════════
 * 有订阅 **或** 今天有单子 → 今日商机;否则 → 去哪里找。
 *
 * ★★ 为什么不无脑落在今日商机:新租户手上零线索、零订阅,那一页对他**永远是空的**。
 *   一个空页面不会教他"去订阅一下",只会教他"这个功能没东西"。
 *   而「去哪里找」摆的是 15 种可以现在就试一次的地方 —— 那是他此刻唯一能做的动作。
 * ★ 判据里**「有订阅」在前**:订阅了但今天恰好没单子,不能把人踢回目录页 ——
 *   那会让他以为自己的订阅没生效,然后再订一次。
 * ★ 两口**并发拉**并把数据递给子页,子页不再拉第二遍:分流本身要读这两份数据,
 *   读完扔掉再让子页重拉,等于首屏白等两跳。
 * ═══════════════════════════════════════════════════════════════════════════
 */
export default function AcqLanding() {
  // 1005:用户**点了**「今日商机」页签(AcqShell 的 NavLink 带 state)→ 就给今日商机的页面(空态说清楚),
  // 不再在「今日商机」页签高亮的情况下摆一整页「去哪里找」目录。
  const location = useLocation();
  const explicitToday = (location.state as { acqTab?: string } | null)?.acqTab === "today";
  const [today, setToday] = useState<AcqTodayView | null>(null);
  const [catalog, setCatalog] = useState<AcqCatalogView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // B2 #20:任一口回 403 MODULE_NOT_ENABLED = 公司没开通获客舱 → 直接落 403 页,不渲染舱壳 + 后端原句。
  // OEM C4:记原因(PAUSED = 被服务商暂停 / NOT_OWNED = 未开通),落 403 页分话术。
  const [blocked, setBlocked] = useState<ModuleBlockReason | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [t, c] = await Promise.allSettled([acqService.today(), acqService.catalog()]);
    const blockReason =
      (t.status === "rejected" ? moduleBlockReasonOf(t.reason) : null) ??
      (c.status === "rejected" ? moduleBlockReasonOf(c.reason) : null);
    if (blockReason) {
      setBlocked(blockReason);
      setLoading(false);
      return;
    }
    const tv = t.status === "fulfilled" ? t.value : null;
    const cv = c.status === "fulfilled" ? c.value : null;
    setToday(tv);
    setCatalog(cv);
    // ★ 只有**两口都挂**才算加载失败。挂一口还能分流:今日商机挂了就按订阅判,
    //   目录挂了就按有没有单子判 —— 半份数据也比一个"什么都别看了"的错误页有用。
    setError(
      tv == null && cv == null
        ? t.status === "rejected" && t.reason instanceof Error
          ? t.reason.message
          : "获客舱没加载出来"
        : null
    );
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (blocked) return <Navigate to={forbiddenPathFor(blocked)} replace />;

  if (loading) {
    return (
      <AcqShell subtitle="正在看你今天有什么可做的…">
        <div className="space-y-4">
          <Skeleton className="h-24 w-full rounded-xl" />
          <Skeleton className="h-64 w-full rounded-xl" />
        </div>
      </AcqShell>
    );
  }

  if (error) {
    return (
      <AcqShell subtitle="没加载出来">
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {error}
          <button
            type="button"
            onClick={() => void load()}
            className="ml-2 font-semibold underline"
          >
            重试
          </button>
        </div>
      </AcqShell>
    );
  }

  const hasSubscription = !!catalog?.items.some((i) => i.subscribed);
  const hasToday = (today?.items.length ?? 0) > 0;

  if (hasSubscription || hasToday || explicitToday) {
    return <TodayPage initialToday={today} catalogItems={catalog?.items ?? null} />;
  }
  // 1005 生产走查:零订阅 + 零单子时此前在 /ai-huoke 原地渲染目录 ——「今日商机」页签高亮,内容却是
  // 「去哪里找」,对不上。新用户仍先看目录(理由见头注),但地址换成 /ai-huoke/discover,页签跟着对上。
  return <Navigate to="/ai-huoke/discover" replace />;
}
